import { useIsMutating } from "@tanstack/react-query";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  TApplicationField,
  useAddNewOption,
  useApplicationFieldOptions,
  usePatchApplicationField,
} from "../../hooks/application-fields.hooks";
import { Dialog, DialogProps } from "../CommonComponents/Dialog/Dialog";
import Input from "../CommonComponents/Input/Input";
import { Menu } from "../CommonComponents/Menu/Menu";
import Select from "../CommonComponents/Select/Select";
import Switch from "../CommonComponents/Switch/Switch";

type OnSubmit = (_: {
  label?: string;
  placeholder?: string;
  active?: boolean;
  mandatory?: boolean;
  options?: number[];
  format?: string;
}) => unknown;

function GenericEdit({
  field,
  onSubmit,
}: {
  field: TApplicationField;
  onSubmit: OnSubmit;
}) {
  const isUpdating = useIsMutating() > 0;
  const { register, formState, handleSubmit } = useForm({
    defaultValues: {
      id: field.id,
      label: field.label,
      labelHindi: field.labelHindi || "",
      labelMarathi: field.labelMarathi || "",
      placeholder: field.placeholder || "",
      active: field?.active || false,
      mandatory: field?.mandatory || false,
    },
  });

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-wrap grow pb-4 px-4"
    >

      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          placeholder="Label Name"
          label="Enter Label Name"
          {...register("label", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
          error={!!formState.errors?.label}
          helperText={formState?.errors?.label?.message}
          containerClass="w-full"
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          placeholder="Placeholder"
          label="Input Box Placeholder"
          containerClass="w-full"
          {...register("placeholder", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name in Hindi"
          label="Enter Label Name in Hindi"
          {...register("labelHindi")}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name in Marathi"
          label="Enter Label Name in Marathi"
          {...register("labelMarathi")}
        />
      </div>
      {formState.defaultValues?.id !== 8 && <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-start md:justify-center space-x-8 items-center">
        <label htmlFor="mandatory" className="font-semibold">
          Mandatory
        </label>
        <Switch id="mandatory" {...register("mandatory")} />
      </div>}
      {formState.defaultValues?.id !== 8 && <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-start md:justify-center space-x-8 items-center">
        <label htmlFor="active" className="font-semibold">
          Field Activate
        </label>
        <Switch id="active" {...register("active")} />
      </div>}
      <div className="p-4 lg:p-6 basis-full flex justify-center items-center">
        <button
          type="submit"
          className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          disabled={isUpdating}
        >
          Update
        </button>
      </div>
    </form>
  );
}

function EditForSelectables({
  field,
  onSubmit,
}: {
  field: TApplicationField;
  onSubmit: OnSubmit;
}) {
  const isUpdating = useIsMutating() > 0;
  const { mutate, isPending } = useAddNewOption();
  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [optionToAdd, setOptionToAdd] = useState("");
  const { isFetching, data } = useApplicationFieldOptions(field.id);

  const options = data?.data || [];

  const [enabledOptions, setEnabledOptions] = useState<number[]>([]);

  useEffect(() => {
    if (!isFetching && options.length) {
      setEnabledOptions((p) =>
        p.length ? p : options.filter((e) => e.active).map((e) => e.id)
      );
    }
  }, [isFetching, options]);

  const { register, formState, handleSubmit } = useForm({
    defaultValues: {
      label: field.label,
      labelHindi: field.labelHindi || "",
      labelMarathi: field.labelMarathi || "",
      placeholder: field.placeholder || "",
      active: field?.active || false,
      mandatory: field?.mandatory || false,
    },
  });

  const toggleOption = (id: number) =>
    setEnabledOptions((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );

  const onAddNewOption = () =>
    mutate(
      { fieldId: field.id, name: optionToAdd },
      {
        onSuccess: (data) => {
          setOptionToAdd("");
          if (data?.optionId) setEnabledOptions((p) => [...p, data.optionId]);
        },
      }
    );

  const inputFieldToAdd =
    field.type !== "configurable-select" ? null : (
      <div className="flex space-x-4 justify-between">
        <Input
          value={optionToAdd}
          disabled={isPending}
          placeholder="Add Option"
          onChange={(e) => setOptionToAdd(e.target.value)}
        />
        <button
          onClick={onAddNewOption}
          disabled={!optionToAdd || isPending || optionToAdd.length > 200}
          className="py-2 px-6 font-semibold bg-primary disabled:bg-gray-300 text-white rounded-lg"
        >
          Add
        </button>
      </div>
    );

  return (
    <form
      noValidate
      onSubmit={handleSubmit((data) =>
        onSubmit({ ...data, options: enabledOptions })
      )}
      className="flex flex-wrap grow"
    >
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          label="Enter Placeholder"
          placeholder="Placeholder"
          {...register("placeholder", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name"
          label="Enter Label Name"
          {...register("label", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
          error={!!formState.errors?.label}
          helperText={formState?.errors?.label?.message?.toString()}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name in Hindi"
          label="Enter Label Name in Hindi"
          {...register("labelHindi")}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name in Marathi"
          label="Enter Label Name in Marathi"
          {...register("labelMarathi")}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Select
          containerClass="w-full"
          value={field.name}
          containerRef={anchor}
          label="Select Options"
          onClick={(e) => setOpen(true)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <option value={field.name}>{field.name}</option>
        </Select>
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex flex-wrap md:flex-nowrap gap-4 justify-start md:justify-evenly items-center">
        <div className="flex space-x-4 basis-full md:basis-auto justify-start md:justify-between">
          <label htmlFor="mandatory" className="font-semibold">
            Mandatory
          </label>
          <Switch id="mandatory" {...register("mandatory")} />
        </div>
        <div className="flex space-x-4 basis-full md:basis-auto justify-start md:justify-between">
          <label htmlFor="active" className="font-semibold">
            Field Activate
          </label>
          <Switch id="active" {...register("active")} />
        </div>
      </div>
      <div className="p-4 basis-full flex justify-center items-center">
        <button
          type="submit"
          disabled={isUpdating}
          className="bg-primary px-6 py-2 rounded-full font-bold text-white"
        >
          Update
        </button>
      </div>
      <Menu anchor={anchor.current} open={open} onClose={() => setOpen(false)}>
        <div className="h-32 lg:h-48 overflow-auto space-y-2 p-4">
          {inputFieldToAdd}
          {(options || []).map((e) => (
            <div className="flex justify-between">
              <p className="text-grey-600">{e.name}</p>
              <Switch
                checked={enabledOptions.includes(e.id)}
                onChange={() => toggleOption(e.id)}
              />
            </div>
          ))}
        </div>
      </Menu>
    </form>
  );
}

function EditForDate({
  field,
  onSubmit,
}: {
  field: TApplicationField;
  onSubmit: OnSubmit;
}) {
  const isUpdating = useIsMutating() > 0;

  const { register, formState, handleSubmit } = useForm({
    defaultValues: {
      label: field.label,
      placeholder: field.placeholder || "",
      active: field?.active || false,
      labelHindi: field.labelHindi ?? "",
      labelMarathi: field?.labelMarathi ?? "",
      mandatory: field?.mandatory || false,
      format: field?.format || "DD/MM/YYYY",
    },
  });

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-wrap grow"
    >
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Placeholder"
          label="Enter Placeholder"
          {...register("placeholder", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name"
          label="Enter Label Name"
          {...register("label", {
            required: "* Required",
            maxLength: {
              message: "Cannot have more than 200 characters",
              value: 200,
            },
          })}
          error={!!formState.errors?.label}
          helperText={formState?.errors?.label?.message?.toString()}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name in Hindi"
          label="Enter Label Name in Hindi"
          {...register("labelHindi")}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-center items-center">
        <Input
          containerClass="w-full"
          placeholder="Label Name in Marathi"
          label="Enter Label Name in Marathi"
          {...register("labelMarathi")}
        />
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex justify-start md:justify-center items-center">
        <div className="flex space-x-4 justify-between">
          <label htmlFor="mandatory" className="font-semibold">
            Mandatory
          </label>
          <Switch id="mandatory" {...register("mandatory")} />
        </div>
      </div>
      <div className="p-4 lg:p-6 basis-full lg:basis-1/2 flex flex-wrap space-x-4 justify-start md:justify-center items-center">
        <div className="flex space-x-4 justify-between">
          <label htmlFor="active" className="font-semibold">
            Field Activate
          </label>
          <Switch id="active" {...register("active")} />
        </div>
      </div>
      <div className="p-4 basis-full flex justify-center items-center">
        <button
          type="submit"
          disabled={isUpdating}
          className="bg-primary px-6 py-2 rounded-full font-bold text-white"
        >
          Update
        </button>
      </div>
    </form>
  );
}

export default function EditApplicationFieldDialog({
  field,
  ...dialogProps
}: {
  field: TApplicationField;
} & Omit<DialogProps, "className">) {
  const patchField = usePatchApplicationField();

  let toRender: ReactNode = null;

  const onSubmit: OnSubmit = (data) => {
    patchField.mutate(
      { id: field.id, ...data },
      { onSuccess: dialogProps.onClose }
    );
  };

  switch (field.type) {
    case "select":
    case "configurable-select":
      toRender = <EditForSelectables field={field} onSubmit={onSubmit} />;
      break;

    case "date":
      toRender = <EditForDate field={field} onSubmit={onSubmit} />;
      break;

    default:
      toRender = <GenericEdit field={field} onSubmit={onSubmit} />;
  }

  return (
    <Dialog {...dialogProps}>
      <div className="p-4 flex justify-center items-center">
        <h4 className="m-0 grow text-center text-2xl font-bold mt-4">
          Edit Field
        </h4>
        <button className="text-xl" onClick={dialogProps.onClose}>
          <i className="fa fa-close"></i>
        </button>
      </div>
      <hr className="p-2 mx-4" />
      {toRender}
    </Dialog>
  );
}
